/**
 * Normalize an error coming back from axios / FastAPI into a plain string.
 *
 * FastAPI 422 returns:  detail: [{type, loc, msg, input, url}, ...]
 * FastAPI 4xx custom:    detail: "some string"
 * Network failure:       no response at all
 *
 * Rendering the array form directly into a React node (toast, <p>{err}</p>, etc.)
 * crashes the page with "Objects are not valid as a React child".
 */
export const errorMessage = (e, fallback = "Request failed. Try again.") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // Format Pydantic validation errors: "field_name: must be > 0"
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        const loc = Array.isArray(d?.loc) ? d.loc.slice(-1)[0] : d?.loc;
        const msg = d?.msg ?? "invalid value";
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") {
    // Single object shape — extract msg if present
    if (typeof detail.msg === "string") return detail.msg;
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  if (e?.message && typeof e.message === "string") return e.message;
  return fallback;
};

/**
 * Returns true only when every field the simulation engine cares about is a
 * sane, finite, positive number AND ages obey current < retirement < life.
 * Mirrors backend Pydantic bounds so we never fire a guaranteed-422 request.
 */
export const arePlanInputsValid = (inputs) => {
  if (!inputs) return false;
  const nums = [
    inputs.current_age,
    inputs.retirement_age,
    inputs.life_expectancy,
    inputs.current_savings,
    inputs.monthly_contribution,
    inputs.annual_withdrawal,
    inputs.inflation_rate,
  ];
  for (const n of nums) {
    if (n === null || n === undefined || n === "") return false;
    const v = Number(n);
    if (!Number.isFinite(v) || v < 0) return false;
  }
  const c = Number(inputs.current_age);
  const r = Number(inputs.retirement_age);
  const l = Number(inputs.life_expectancy);
  if (c < 18 || c > 99) return false;
  if (r < 30 || r > 99) return false;
  if (l < 60 || l > 120) return false;
  if (c >= r) return false;
  if (r >= l) return false;
  return true;
};
